import { Controller } from '@hotwired/stimulus';
import {
  applyCaseExperienceState,
  patchDocumentUserState,
  normalizeNodeUid,
  resolveAnswerCommentSectionElement,
  findAnswerCommentInActivePane,
  revealAnswerCommentElement,
} from '../../utils/case_experience/case_experience_in_rails';
import {
  anyMcqAnswersChecked,
  applyDerivedPassiveSubmitInactive,
} from '../../utils/case_experience/passive_submit_eligibility';

export default class extends Controller {
  static targets = ['answer'];
  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    multipleSelect: Boolean,
    inactive: Boolean,
    graded: Boolean,
  };

  connect() {
    this._selectionSaveInFlight = 0;
    if (this.answerTargets.length === 0) {
      return;
    }
    this.submitButtonContainer = this.findSubmitButtonContainer();
    if (this.submitButtonContainer) {
      const submitButton = this.submitButtonContainer.querySelector('button');
      if (submitButton) {
        this.submitButton = submitButton;
        this._handleSubmit = this.handleSubmit.bind(this);
        this.submitButton.addEventListener('click', this._handleSubmit);
      }
    }

    if (this.gradedValue) {
      this.disableSubmitButton();
      this.applyPostGradedDomState();
      this.lockSubmitButtonForGraded();
      this.revealAnswerComment();
    } else {
      this.updateSubmitButtonState();
    }
  }

  disableSubmitButton() {
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
    }
  }

  lockSubmitButtonUntilPersisted() {
    const submitButton = this.resolveSubmitButton();
    const submitSlice = submitButton?.closest('.doc-button-slice');
    applyDerivedPassiveSubmitInactive({
      submitSlice,
      application: this.application,
      buttonActionIdentifier: 'case-experience--button-action',
      enabled: false,
      submitButton,
    });
  }

  lockSubmitButtonForGraded() {
    const slice = this.submitButtonContainer || this.findSubmitButtonContainer();
    if (!slice) return;
    slice.setAttribute('data-case-experience--button-action-inactive-value', '2');
    const buttonAction = this.application?.getControllerForElementAndIdentifier(
      slice,
      'case-experience--button-action',
    );
    if (buttonAction) {
      buttonAction.inactiveValue = 2;
    }
    this.disableSubmitButton();
  }

  resolveAnswerCommentUid() {
    const parent = this.element.parentElement;
    if (parent) {
      for (const child of parent.children) {
        const slotUid = child.dataset?.uid;
        if (slotUid?.endsWith('_ANS_COMMENT')) {
          return slotUid;
        }
        if (child.id?.startsWith('slot-') && child.id.endsWith('_ANS_COMMENT')) {
          return child.id.replace(/^slot-/, '');
        }
        if (child.classList?.contains('answer_comment')) {
          return child.dataset?.uid || child.id;
        }
        if (child.hasAttribute('data-slot') && slotUid) {
          return slotUid;
        }
      }
    }
    return `${normalizeNodeUid(this.nodeUidValue)}_ANS_COMMENT`;
  }

  revealAnswerComment() {
    const answerComment =
      resolveAnswerCommentSectionElement(this.element, this.nodeUidValue) ||
      findAnswerCommentInActivePane(this.element);
    if (answerComment) {
      revealAnswerCommentElement(answerComment);
    }
  }

  buildSubmitState(updatedAt) {
    const questionUid = normalizeNodeUid(this.nodeUidValue);
    const answerCommentUid = this.resolveAnswerCommentUid();
    const submitSlice = this.submitButtonContainer || this.findSubmitButtonContainer();
    const submitUid = submitSlice?.dataset?.uid;

    const state = {};
    state[questionUid] = {
      graded: { value: true, updatedAt },
      inactive: { value: 2, updatedAt },
    };
    state[answerCommentUid] = {
      invisible: { value: false, updatedAt },
    };
    if (submitUid) {
      state[submitUid] = {
        inactive: { value: 2, updatedAt },
      };
    }
    return state;
  }

  applyPostGradedDomState() {
    this.revealAnswerComment();
    this.answerTargets.forEach((target) => {
      const input =
        target.querySelector('input[type="checkbox"], input[type="radio"]') ||
        target.querySelector('[role="checkbox"]');
      if (input) {
        input.setAttribute('aria-disabled', 'true');
        input.tabIndex = -1;
        if (input instanceof HTMLInputElement) {
          input.disabled = true;
        }
      }
    });
    this.disableSubmitButton();
  }

  findSubmitButtonContainer() {
    const section = this.element.closest('.doc-section');
    if (section) {
      const slices = section.querySelectorAll('.doc-button-slice');
      for (let i = 0; i < slices.length; i += 1) {
        const slice = slices[i];
        const name = (slice.getAttribute('data-case-experience--button-action-name-value') || '')
          .trim()
          .toLowerCase();
        if (name !== 'submit') continue;

        const pos = this.element.compareDocumentPosition(slice);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
          return slice;
        }
      }
    }

    let sib = this.element?.nextElementSibling;
    while (sib) {
      if (sib.classList?.contains('doc-button-slice')) {
        const name = (sib.getAttribute('data-case-experience--button-action-name-value') || '')
          .trim()
          .toLowerCase();
        if (name === 'submit') {
          return sib;
        }
      }
      sib = sib.nextElementSibling;
    }

    return null;
  }

  resolveSubmitButton() {
    if (this.submitButton) {
      return this.submitButton;
    }
    const container = this.findSubmitButtonContainer();
    return container ? container.querySelector('button') : null;
  }

  disconnect() {
    if (this.submitButtonContainer && this.submitButton && this._handleSubmit) {
      this.submitButton.removeEventListener('click', this._handleSubmit);
    }
    this.submitButton = null;
    this._handleSubmit = null;
    this.submitButtonContainer = null;
  }

  async handleSubmit() {
    try {
      const updatedAt = Date.now();
      const state = this.buildSubmitState(updatedAt);
      await applyCaseExperienceState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
        body: { document_user_state: { state } },
      });
      this.inactiveValue = true;
      this.gradedValue = true;
      this.applyPostGradedDomState();
      this.lockSubmitButtonForGraded();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.revealAnswerComment();
          this.lockSubmitButtonForGraded();
        });
      });
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectAnswer(event);
    }
  }

  async selectAnswer(event) {
    if (this.inactiveValue) return;
    if (this._selectAnswerHandling) return;
    this._selectAnswerHandling = true;

    const answerElement =
      event.currentTarget.closest('[data-case-experience--multiple-choice-target="answer"]') || event.currentTarget;
    const answerUid = answerElement.dataset.answerUid;
    const isCurrentlyChecked = answerElement.classList.contains('checked');

    const updatedAt = Date.now();
    const state = {};

    if (!this.multipleSelectValue && !isCurrentlyChecked) {
      this.answerTargets.forEach((target) => {
        const uid = target.dataset.answerUid;
        if (target !== answerElement) {
          target.classList.remove('checked');
          target.setAttribute('aria-checked', 'false');
          const input = target.querySelector('input[type="checkbox"], input[type="radio"]');
          if (input) {
            input.checked = false;
            input.setAttribute('aria-checked', 'false');
          }
          state[uid] = {};
          state[uid]['value'] = { value: false, updatedAt };
        }
      });
    }

    const newCheckedState = !isCurrentlyChecked;
    answerElement.classList.toggle('checked', newCheckedState);
    answerElement.setAttribute('aria-checked', newCheckedState);
    const input = answerElement.querySelector('input[type="checkbox"], input[type="radio"]');
    if (input) {
      input.checked = newCheckedState;
      input.setAttribute('aria-checked', String(newCheckedState));
    }

    state[answerUid] = {};
    state[answerUid]['value'] = { value: newCheckedState, updatedAt };

    this.lockSubmitButtonUntilPersisted();
    this._selectionSaveInFlight += 1;

    try {
      const response = await patchDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
        body: {
          document_user_state: { state },
        },
      });

      if (!response) {
        throw new Error('Failed to save answer selection');
      }
    } catch (error) {
      console.error('Error selecting answer:', error);
      answerElement.classList.toggle('checked', isCurrentlyChecked);
      answerElement.setAttribute('aria-checked', isCurrentlyChecked);
      const rollbackInput = answerElement.querySelector(
        'input[type="checkbox"], input[type="radio"]',
      );
      if (rollbackInput) {
        rollbackInput.checked = isCurrentlyChecked;
        rollbackInput.setAttribute('aria-checked', String(isCurrentlyChecked));
      }
    } finally {
      this._selectAnswerHandling = false;
      this._selectionSaveInFlight = Math.max(0, this._selectionSaveInFlight - 1);
      if (this._selectionSaveInFlight === 0) {
        this.updateSubmitButtonState();
      }
    }
  }

  updateSubmitButtonState() {
    if (this.gradedValue || this.inactiveValue) {
      this.lockSubmitButtonForGraded();
      return;
    }

    const nextSubmitButton = this.resolveSubmitButton();
    const submitSlice = nextSubmitButton?.closest('.doc-button-slice');
    const anySelected = anyMcqAnswersChecked(this.answerTargets);

    applyDerivedPassiveSubmitInactive({
      submitSlice,
      application: this.application,
      buttonActionIdentifier: 'case-experience--button-action',
      enabled: anySelected,
      submitButton: nextSubmitButton,
    });
  }
}
